import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { PageHeader } from "@/mockups/ui";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Save, Info } from "lucide-react";

const MODULE_LABELS = {
  dashboard: "Dashboard",
  crm: "CRM · Empresas / Contactos / Proveedores",
  projects: "Proyectos",
  tasks: "Tareas",
  timesheets: "Horas",
  external_payments: "Pagos externos",
  hr: "RRHH · Fichas / Vacaciones / Nóminas",
  users: "Usuarios",
  settings: "Ajustes / Permisos",
};
const ACTION_LABELS = { view: "Ver", create: "Crear", edit: "Editar", delete: "Eliminar" };

export default function Permisos() {
  const { data, loading } = useFetch("/roles/permissions");
  const [role, setRole] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data && !role) setRole(data.roles.find((r) => r !== "Administrador") || data.roles[0]);
  }, [data, role]);

  useEffect(() => {
    if (data && role) setMatrix(JSON.parse(JSON.stringify(data.permissions[role])));
  }, [data, role]);

  if (loading || !data || !role || !matrix) {
    return <div className="flex justify-center py-20"><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;
  }

  const isAdmin = role === "Administrador";
  const toggle = (mod, act) => {
    setMatrix((m) => ({ ...m, [mod]: { ...m[mod], [act]: !m[mod][act] } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/roles/${encodeURIComponent(role)}/permissions`, { permissions: matrix });
      toast.success(`Permisos de ${role} guardados`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader title="Matriz de permisos" subtitle="Configura qué puede hacer cada rol, por módulo y acción">
        {!isAdmin && <Button size="sm" className="gap-1.5" onClick={save} disabled={saving} data-testid="save-permissions-btn"><Save className="h-4 w-4" /> {saving ? "Guardando…" : "Guardar cambios"}</Button>}
      </PageHeader>

      <div className="flex flex-wrap gap-2">
        {data.roles.map((r) => (
          <button key={r} onClick={() => setRole(r)} data-testid={`role-tab-${r}`}
            className={cn("px-4 py-2 rounded-md text-sm font-medium border transition-colors duration-200",
              role === r ? "bg-navy text-white border-navy" : "bg-card border-border text-muted-foreground hover:text-foreground hover:border-muted-foreground/30")}>
            {r}
          </button>
        ))}
      </div>

      {isAdmin && (
        <div className="flex items-center gap-2 rounded-md bg-primary/8 border border-primary/20 px-4 py-3 text-sm text-primary">
          <Info className="h-4 w-4 shrink-0" />
          El rol <strong>Administrador</strong> tiene acceso total y no se puede restringir.
        </div>
      )}

      <Card className="border-border shadow-none overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider px-5 py-3.5">Módulo</th>
              {data.actions.map((a) => <th key={a} className="text-center text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 py-3.5 w-24">{ACTION_LABELS[a]}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.modules.map((mod) => (
              <tr key={mod} className="hover:bg-muted/30 transition-colors duration-200" data-testid={`perm-row-${mod}`}>
                <td className="px-5 py-3.5 text-sm font-medium">{MODULE_LABELS[mod] || mod}</td>
                {data.actions.map((act) => (
                  <td key={act} className="px-4 py-3.5 text-center">
                    <div className="flex justify-center">
                      <Switch
                        checked={isAdmin ? true : !!matrix[mod]?.[act]}
                        disabled={isAdmin}
                        onCheckedChange={() => toggle(mod, act)}
                        data-testid={`perm-${role}-${mod}-${act}`}
                      />
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
